import { Clinic } from '@app/core/models';
import { initialVetState } from './vet.state';
import { createReducer, on } from '@ngrx/store';
import { replaceInArray } from '@app/shared/utils/static-helpers/array.utils';
import {
  getFeatureTogglesDataSuccess,
  getMarketSuccess,
  getRecoConsentDataSuccess,
  getVetSuccess,
  setCurrentBigMeasurementUnit,
  setCurrentClinicId,
  setCurrentSmallMeasurementUnit,
  setCurrentSystemPreferenceCode,
  setInvitation,
  setPreferredMaxPetWeight,
  setVet,
  setVetValidity,
  updateClinicSuccess,
  updateVetSuccess,
  updateVetUserSuccess,
} from './vet.actions';
import { FeatureToggle } from '@app/core/models/feature-toggles';
/**
 * Vet reducer
 */
export const vetReducer = createReducer(
  initialVetState,
  on(setVet, (state, { vet }) => ({
    ...state,
    vet: {
      ...state.vet,
      ...vet,
    },
  })),
  on(updateVetSuccess, (state, { vet }) => ({
    ...state,
    vet: {
      ...state.vet,
      ...vet,
    },
  })),
  on(updateVetUserSuccess, (state, { vetUser }) => ({
    ...state,
    vet: {
      ...state.vet,
      user: {
        ...state.vet.user,
        ...vetUser,
      },
    },
  })),
  on(setCurrentClinicId, (state, { clinicId }) => ({
    ...state,
    currentClinicId: clinicId,
  })),
  on(getVetSuccess, (state, { vet, currentClinic, oktaUser }) => ({
    ...state,
    vet,
    oktaUser,
    currentClinicId: currentClinic?.id,
  })),
  on(setVetValidity, (state, { value }) => ({
    ...state,
    isVetValid: value,
  })),
  on(setInvitation, (state, { invitation }) => ({
    ...state,
    invitation,
  })),
  on(getMarketSuccess, (state, { market }) => ({
    ...state,
    market,
  })),
  on(getRecoConsentDataSuccess, (state, { recoConsent, countryCode }) => ({
    ...state,
    hasRecoSharingConsent: recoConsent?.consentedCountries?.includes(countryCode) ?? false,
  })),
  on(getFeatureTogglesDataSuccess, (state, { featureToggles, customerId, countryCode }) => {
    let features: FeatureToggle[] = [];
    const featuresPerCustomer = featureToggles[customerId];
    // If feature toggles were found for this specific customer, use them:
    if (featuresPerCustomer?.length) {
      features = featuresPerCustomer;
    } else {
      // Otherwise, use the feature toggles for the market (if any):
      const featuresPerMarket = featureToggles[`MARKET:${countryCode}`];
      if (featuresPerMarket?.length) {
        features = featuresPerMarket;
      }
    }
    return {
      ...state,
      featureToggles: features,
    };
  }),
  on(updateClinicSuccess, (state, { clinic }) => {
    const newClinicWithOldRoles = addRolesToUpdatedClinic(state.vet.clinics, clinic);
    return {
      ...state,
      vet: {
        ...state.vet,
        clinics: replaceInArray(state.vet.clinics, newClinicWithOldRoles, (item) => item.id, false),
      },
    };
  }),
  on(setPreferredMaxPetWeight, (state, { weight }) => ({
    ...state,
    preferences: {
      ...state.preferences,
      maxPetWeight: weight,
    },
  })),
  on(setCurrentBigMeasurementUnit, (state, { unit }) => ({
    ...state,
    preferences: {
      ...state.preferences,
      currentBigMeasurementUnit: unit,
    },
  })),
  on(setCurrentSmallMeasurementUnit, (state, { unit }) => ({
    ...state,
    preferences: {
      ...state.preferences,
      currentSmallMeasurementUnit: unit,
    },
  })),
  on(setCurrentSystemPreferenceCode, (state, { systemPreferenceCode }) => ({
    ...state,
    preferences: {
      ...state.preferences,
      currentSystemPreferenceCode: systemPreferenceCode,
    },
  })),
);

function addRolesToUpdatedClinic(clinics: Clinic[], clinic: Clinic): Clinic {
  const oldClinic = clinics.find((elem) => elem.id === clinic.id);
  return { ...clinic, companyRoles: oldClinic?.companyRoles || [] };
}
